import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  SelectField,
  TextField,
  toast,
} from '@eon/ui';
import { hooks, type CreateIdTagInput } from '@eon/api-client';
import { KeyRound } from 'lucide-react';

const MAX_TX_OPTIONS = [-1, 0, 1, 2, 3, 5, 10] as const;
const NONE = '__none__';

interface CreateForm {
  userId: string;
  tag: string;
  description: string;
  parentIdTag: string;
  expiryDate: string;
  maxActiveTransactions: number;
}

interface CreateIdTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateIdTagDialog({ open, onOpenChange }: CreateIdTagDialogProps) {
  const { t } = useTranslation();
  const create = hooks.useCreateIdTag();
  const users = hooks.useAdminUsers({ page: 1, pageSize: 200 });
  const tags = hooks.useAdminIdTags({ page: 1, pageSize: 200 });

  const { register, handleSubmit, setValue, watch, formState, reset } = useForm<CreateForm>({
    defaultValues: {
      userId: NONE,
      tag: '',
      description: '',
      parentIdTag: NONE,
      expiryDate: '',
      maxActiveTransactions: -1,
    },
  });

  const userOptions = [
    { value: NONE, label: t('idTag.selectUser') },
    ...(users.data?.data ?? []).map((u) => ({
      value: u.id,
      label: u.name || u.email || u.id,
    })),
  ];

  const parentOptions = [
    { value: NONE, label: t('idTag.noParent') },
    ...(tags.data?.data ?? []).map((tag) => ({
      value: tag.id,
      label: `${tag.name} (${tag.value})`,
    })),
  ];

  const maxTxOptions = MAX_TX_OPTIONS.map((n) => ({
    value: String(n),
    label: t(`idTag.maxTx.${n}`),
  }));

  const onSubmit = handleSubmit(async (values) => {
    if (!values.userId || values.userId === NONE) {
      toast.error(t('idTag.userRequired'));
      return;
    }
    const payload: CreateIdTagInput = {
      value: values.tag.trim(),
      name: values.tag.trim(),
      userId: values.userId,
      isPhysical: true,
      description: values.description.trim() || undefined,
      parentIdTag: values.parentIdTag === NONE ? undefined : values.parentIdTag,
      expiryDate: values.expiryDate || undefined,
      maxActiveTransactions: values.maxActiveTransactions,
    };
    try {
      await create.mutateAsync(payload);
      toast.success(t('idTag.created'));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('idTag.createTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label={t('idTag.owner')}>
            <SelectField
              value={watch('userId') || NONE}
              options={userOptions}
              onValueChange={(v) => setValue('userId', v, { shouldValidate: true })}
            />
          </Field>
          <Field label={t('idTag.tagType')}>
            <p className="text-muted-foreground text-sm">{t('idTag.physicalOnly')}</p>
          </Field>
          <Field label={t('idTag.tag')}>
            <TextField {...register('tag', { required: true })} placeholder="04AB7CDE" />
          </Field>
          <Field label={t('idTag.description')}>
            <TextField {...register('description')} />
          </Field>
          <Field label={t('idTag.parent')}>
            <SelectField
              value={watch('parentIdTag') || NONE}
              options={parentOptions}
              onValueChange={(v) => setValue('parentIdTag', v)}
            />
          </Field>
          <Field label={t('idTag.expiry')}>
            <TextField type="date" {...register('expiryDate')} />
          </Field>
          <Field label={t('idTag.maxActiveTx')}>
            <SelectField
              value={String(watch('maxActiveTransactions'))}
              options={maxTxOptions}
              onValueChange={(v) => setValue('maxActiveTransactions', Number(v))}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || create.isPending}>
              <KeyRound className="mr-2 h-4 w-4" />
              {t('actions.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
